import Button from "@edaz/edazkit/Button/index";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist";
import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views/dist";
import { observer } from "mobx-react-lite";
import React from "react";
import { ApprovalColumns } from "views/common/approvalColumns";
import EditableCell from "views/common/editableCell";
import ListView from "views/common/list";
import type InternalTransfer from "entities/internalTransferResponse";
import { formatAmount, translateGeneral } from "utils/localizations";
import InternalTransfersViewModel from "viewModels/finance/internalTransfers/internalTransfersViewModel";
import InternalTransfersFilters from "./internalTransfersFilters";

const InternalTransfersView: ViewComponent<InternalTransfersViewModel> = observer(({ vm }) => {
  const columns: ColumnDefinition<InternalTransfer>[] = [
    {
      title: vm.translateAttribute("senderBankAccount"),
      property: "senderBankAccount",
      valueFormatter: props => formatBankAccount(props.value),
    },
    {
      title: vm.translateAttribute("recipientBankAccount"),
      property: "recipientBankAccount",
      valueFormatter: props => formatBankAccount(props.value),
    },
    {
      title: vm.translateAttribute("messageForRecipient"),
      property: "messageForRecipient",
      valueFormatter: ({ item, column }) => (
        <EditableCell property={column.property} vm={vm.getEditViewModel(item)}>
          {item.messageForRecipient}
        </EditableCell>
      ),
    },
    {
      title: vm.translateAttribute("amount"),
      property: "amount",
      cellClassName: "text-right",
      valueFormatter: ({ item, column }) => (
        <EditableCell property={column.property} vm={vm.getEditViewModel(item)}>
          {formatAmount(item.amount)}
        </EditableCell>
      ),
    },
    {
      title: vm.translateAttribute("variableSymbol"),
      property: "variableSymbol",
    },
    {
      title: vm.translateAttribute("constantSymbol"),
      property: "constantSymbol",
    },
    {
      title: vm.translateAttribute("specificSymbol"),
      property: "specificSymbol",
    },
    {
      title: vm.translateAttribute("month"),
      property: "month",
      valueFormatter: ({ item }) => translateGeneral(`month.${item.month}`),
    },
    ...ApprovalColumns<InternalTransfer, InternalTransfersViewModel>(vm, false),
  ];

  const exportApprovedButton = (
    <Button
      size="sm"
      key="export-approved-internal-transfers"
      variant="primary"
      className="rounded"
      onClick={vm.exportApproved}
      disabled={vm.noApprovalsForExport}>
      {vm.translateModuleGeneral("exportApproved")}
    </Button>
  );

  return (
    <ListView
      aboveTable={<InternalTransfersFilters vm={vm} />}
      showDetailWithList={true}
      columns={columns}
      vm={vm}
      headerButtons={[exportApprovedButton]}
    />
  );
});

registerView(InternalTransfersView, InternalTransfersViewModel);
